import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Code2, Crosshair, History, Info, SlidersHorizontal } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Tabs, TabsList } from '@/components/ui/tabs';
import { useQueryState } from 'hooks/useQueryState';
import { showsGuardrail } from '@/state/flags/flag-guardrail';
import { useFlag } from '@/state/flags/useFlag';
import { useTourScreen } from '@/tours';
import { ActivityEntries } from '../../Experiments/Details/ExperimentSettings/ActivityLog';
import { SCREEN_TABS_LIST_CLASS, ScreenTab } from '../../Experiments/Details/ScreenTab';
import { FlagHeader } from './FlagHeader';
import { FlagStatusCard } from './FlagStatusCard';
import { RolloutControl } from './RolloutControl';
import { RolloutPlan } from './RolloutPlan';
import { FlagGuardrail } from './FlagGuardrail';
import { FlagSchedule } from './FlagSchedule';
import { FlagBrief } from './FlagBrief';
import { FlagServerRules, FlagTargeting } from './FlagTargeting';
import { FlagRings } from './FlagRings';
import { FlagCode } from './FlagCode';
import { FlagEditorChanges } from './FlagEditorChanges';
import { FlagGoal } from './FlagGoal';
import { FlagPayloads } from './FlagPayloads';
import { FlagSection } from './FlagSection';
import { FLAG_COPY } from '../copy';

/** The flag page's tabs, in `?screen=` as on an experiment. Each has a tour of its own. */
export const FLAG_SCREENS = ['rollout', 'brief', 'targeting', 'setup', 'activity'] as const;
export type FlagScreen = (typeof FLAG_SCREENS)[number];

const isFlagScreen = (value: string): value is FlagScreen => (FLAG_SCREENS as readonly string[]).includes(value);

const ICON_CLASS = 'size-3.5';
const TAB_ICONS: Record<FlagScreen, typeof Info> = {
  rollout: SlidersHorizontal,
  brief: Info,
  targeting: Crosshair,
  setup: Code2,
  activity: History,
};

/** Keyed by the flag, so moving from one flag to another starts every editor afresh. */
export const FlagDetailsScreen = () => {
  const { id } = useParams();
  return <FlagDetails key={id} flagId={Number(id)} />;
};

type FlagDetailsProps = { flagId: number };

/**
 * One feature flag, in the experiment details page's frame: the header with the main action in
 * the breadcrumb bar, the status card where an experiment has its Timeline, then the tabs.
 * Rollout is what changes day to day (the share, the plan, its guardrail, the schedule); Brief is
 * what it is for; Targeting who can get it; Setup how it is built and judged; Activity what
 * happened.
 */
const FlagDetails = ({ flagId }: FlagDetailsProps) => {
  const manager = useFlag(flagId);
  const { flag, schedule } = manager;
  const [screenParam, setScreen] = useQueryState<string>('screen', '');
  const screen: FlagScreen = isFlagScreen(screenParam) ? screenParam : 'rollout';
  // Tabs are written with replaceState, which the router never sees, so the open tab is
  // reported to the tour engine: each tab has a tour of its own.
  useTourScreen(flag ? screen : null);

  // An empty or unknown ?screen= lands on Rollout, and says so in the address.
  useEffect(() => {
    if (flag && screenParam !== screen) setScreen(screen);
  }, [flag, screenParam, screen, setScreen]);

  if (manager.isError) {
    return (
      <main className="bg-panel border-line rounded-panel border px-6 py-12">
        <p className="supporting m-0 text-center">{FLAG_COPY.header.loadFailed}</p>
      </main>
    );
  }
  if (!flag) return null;

  const isEditor = manager.isEditor;

  return (
    <div className="flex flex-col gap-[14px]">
      <FlagHeader manager={manager} flag={flag} />
      <FlagStatusCard manager={manager} flag={flag} />

      <Tabs value={screen} onValueChange={(value) => setScreen(value)}>
        {/* Five tabs do not fit a phone's width, so the strip scrolls sideways rather than the page. */}
        <TabsList className={cn(SCREEN_TABS_LIST_CLASS, 'max-w-full justify-start overflow-x-auto')}>
          {FLAG_SCREENS.map((value) => {
            const Icon = TAB_ICONS[value];
            return (
              <ScreenTab
                key={value}
                value={value}
                icon={<Icon className={ICON_CLASS} strokeWidth={1.5} />}
                label={FLAG_COPY.tabs[value]}
              />
            );
          })}
        </TabsList>
      </Tabs>

      {screen === 'rollout' && (
        <>
          <RolloutControl manager={manager} />
          <RolloutPlan manager={manager} />
          {showsGuardrail({ rollout: flag.flagRollout, isArchived: manager.isArchived }) && (
            <FlagGuardrail flag={flag} />
          )}
          {schedule && <FlagSchedule manager={manager} schedule={schedule} />}
        </>
      )}

      {screen === 'brief' && <FlagBrief flag={flag} isEditor={isEditor} />}

      {screen === 'targeting' && (
        <>
          <FlagTargeting manager={manager} flag={flag} />
          {!isEditor && <FlagServerRules manager={manager} flag={flag} />}
          <FlagRings manager={manager} />
        </>
      )}

      {screen === 'setup' && (
        <>
          {isEditor ? <FlagEditorChanges flag={flag} /> : <FlagCode flag={flag} />}
          <FlagGoal manager={manager} flag={flag} />
          {!isEditor && <FlagPayloads flag={flag} />}
        </>
      )}

      {screen === 'activity' && (
        <FlagSection
          title={FLAG_COPY.activity.title}
          help={FLAG_COPY.activity.help}
          description={FLAG_COPY.activity.description}
          tourAnchor="flag-activity"
        >
          <div className="-m-2">
            <ActivityEntries entries={flag.activityLog ?? []} />
          </div>
        </FlagSection>
      )}
    </div>
  );
};
